/**
 * Utility functions for handling printing functionality
 */

/**
 * Prints the content of a div element with proper styling and error handling
 * @param {HTMLElement} element - The element to print
 * @param {Object} options - Additional options for printing
 * @param {string} options.title - Title for the print window
 * @param {string} options.styles - Additional CSS styles to apply
 * @returns {Promise<void>}
 */
export const printElement = async (element, options = {}) => {
  if (!element) {
    console.error('No element provided for printing');
    return;
  }

  try {
    // Create a new window for printing
    const printWindow = window.open('', '_blank', 'height=600,width=800');
    if (!printWindow) {
      throw new Error('Pop-up blocked. Please allow pop-ups for this site.');
    }

    // Get the element's content and styles
    const content = element.innerHTML;
    const styles = Array.from(document.styleSheets)
      .map(styleSheet => {
        try {
          return Array.from(styleSheet.cssRules)
            .map(rule => rule.cssText)
            .join('\n');
        } catch (e) {
          console.warn('Could not read styles from stylesheet:', e);
          return '';
        }
      })
      .join('\n');

    // Write the content to the new window with proper styling
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${options.title || 'Print'}</title>
          <style>
            ${styles}
            @page {
              size: 80mm auto;
              margin: 0;
            }
            body {
              font-family: 'Courier New', monospace;
              margin: 0;
              padding: 0;
              font-size: 12px;
              line-height: 1.2;
              background: white;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .print-ticket {
              width: 80mm;
              margin: 0 auto;
              padding: 5mm;
              background: white;
              box-sizing: border-box;
              color: black !important;
            }
            .ticket-table {
              width: 100%;
              border-collapse: collapse;
              margin: 5px 0;
            }
            .ticket-table th,
            .ticket-table td {
              padding: 2px;
              text-align: left;
              border-bottom: 1px solid #ddd;
            }
            .breaker {
              border-top: 1px dashed #ccc;
              margin: 5px 0;
            }
            .ticket-item {
              display: flex;
              justify-content: space-between;
              margin: 2px 0;
            }
            img {
              max-width: 100%;
              height: auto;
            }
            .mantine-Grid-root {
              margin: 0 !important;
              padding: 0 !important;
            }
            .mantine-Grid-col {
              padding: 0 !important;
            }
            ${options.styles || ''}
          </style>
        </head>
        <body>
          <div class="print-ticket">
            ${content}
          </div>
          <script>
            window.onload = function() {
              // Ensure all images are loaded before printing
              const images = document.getElementsByTagName('img');
              let loadedImages = 0;
              const totalImages = images.length;

              if (totalImages === 0) {
                window.print();
              } else {
                Array.from(images).forEach(img => {
                  if (img.complete) {
                    loadedImages++;
                    if (loadedImages === totalImages) {
                      window.print();
                    }
                  } else {
                    img.onload = function() {
                      loadedImages++;
                      if (loadedImages === totalImages) {
                        window.print();
                      }
                    };
                    img.onerror = function() {
                      loadedImages++;
                      if (loadedImages === totalImages) {
                        window.print();
                      }
                    };
                  }
                });
              }

              window.onafterprint = function() {
                window.close();
              };
            };
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  } catch (error) {
    console.error('Error printing:', error);
    throw error;
  }
};

/**
 * Formats a number as currency
 * @param {number} value - The number to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value) => {
  return Number(value || 0).toFixed(2);
}; 